import React, { useState, FC } from 'react'

import { Text, XStack, YStack, useThemeName } from 'tamagui'

import { Grid } from '@newComponents/Grid'
import { Separator } from '@newComponents/Separator'
import { Link, ExternalLink } from '@newComponents/Link'

import { IconWrapper } from '@newComponents/Icon/IconWrapper'
import WindowNew from '@newComponents/Icon/icons/WindowNew'

import { getPurpleBoxShadow } from './UnauthenticatedHomeScreen/styles'

export const HomeScreenFeaturesCard: FC = () => {
  const isDark = useThemeName() === 'dark'

  return (
    <YStack padding="$6" backgroundColor="$backgroundSoft" borderRadius="$8" style={{ boxShadow: getPurpleBoxShadow(isDark) } as any}>
      <YStack gap="$10">
        <YStack gap="$8" $gtXs={{ display: 'none' }}>
          <GuildsAndNetworks />

          <Events />

          <Presentations />
        </YStack>
        <YStack display="none" $gtXs={{ display: 'flex' }}>
          <Grid gap={6}>
            <Grid.Column>
              <GuildsAndNetworks />
            </Grid.Column>

            <Grid.Column>
              <Events />
            </Grid.Column>

            <Grid.Column>
              <Presentations />
            </Grid.Column>
          </Grid>
        </YStack>

        <YStack alignItems='center'>
          <Separator borderBottomColor="$borderColor" width={60} />
        </YStack>

        <YStack alignItems='center'>
          <Text fontSize="$4" color="$colorMuted">
            Not sure about a feature?
          </Text>

          <Text fontSize="$4" color="$colorMuted" textAlign='center'>
            <ExternalLink href='mailto:hi@guild.host'>
              <Text fontSize="$4" color="$colorMuted">
                Get in touch
              </Text>
            </ExternalLink>
            , we'd love to chat with you!
          </Text>
        </YStack>
      </YStack>
    </YStack>
  )
}

const GuildsAndNetworks: FC = () => {
  const isDark = useThemeName() === 'dark'
  const accentColor = isDark ? '#A78BFA' : '#5533EB'
  const circleBg = isDark ? 'rgba(167, 139, 250, 0.2)' : 'rgba(85, 51, 235, 0.1)'

  return (
    <YStack gap="$4">
      <Text fontSize="$6" fontWeight="600" fontFamily="$body" color={accentColor}>
        Guilds & Networks
      </Text>

      <YStack gap="$2">
        {[
          <Text fontSize='$4'>Create a Following</Text>,
          <Text fontSize='$4'>Manage your Members</Text>,
          <Text fontSize='$4'>3-tier Permissions System</Text>,
          <Text fontSize='$4'>Membership Forms & Approvals</Text>,
          <Text fontSize='$4'>Inter-connect with Networks</Text>,
          <Text fontSize='$4'>Cross-promote via Networks</Text>,
          <Text fontSize='$4'>Accept Donations</Text>,
          <Text fontSize='$4'>Sponsorships & Analytics</Text>,
          <Text fontSize='$4'>Message your Members</Text>,
          <Text fontSize='$4'>Notify your Slack</Text>,
          <Text fontSize='$4'>Embeddable Widgets</Text>,
          <FeatureLinkBulletItem
            href='/integrations/bluesky'
            label='Announce on Bluesky'
          />,
          <FeatureLinkBulletItem
            href='/integrations/meetup'
            label='Mirror your Meetup.com events'
          />,
          <Text fontSize='$4'>Google Calendar Sync</Text>,
          <FeatureLinkBulletItem
            href='/api/next/docs'
            label='API Access'
            external
          />,
        ].map((bullet, index) => (
          <XStack gap="$2" key={index}>
            <XStack width={18} minWidth={18} height={18} minHeight={18} backgroundColor={circleBg} alignItems="center" justifyContent="center" borderRadius={9}>
              <Text color={accentColor}>✓</Text>
            </XStack>

            {bullet}
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}

const FeatureLinkBulletItem = ({
  href,
  label,
  external = false,
}: {
  href: string
  label: string
  external?: boolean
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const WrappingComponent = external ? ExternalLink : Link

  return (
    <WrappingComponent
      href={href}
      display='flex'
      gap='$2'
      alignItems='center'
      flex={1}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      size='$4'
      animation='medium'
      color='$color'
      hoverStyle={{ color: '$primaryColor', textDecorationLine: 'none' }}
    >
      {label}

      <XStack animation='medium' x={isHovered ? 4 : 0} width='$4' height='$4'>
        <XStack opacity={0.6} position='absolute'>
          <IconWrapper size='$4' color='$color'>
            <WindowNew />
          </IconWrapper>
        </XStack>

        <XStack
          animation='medium'
          opacity={isHovered ? 1 : 0}
          position='absolute'
        >
          <IconWrapper size='$4' color='$primaryColor'>
            <WindowNew />
          </IconWrapper>
        </XStack>
      </XStack>
    </WrappingComponent>
  )
}

const Events: FC = () => {
  const isDark = useThemeName() === 'dark'
  const accentColor = isDark ? '#F5A0F6' : '#F26AF4'
  const circleBg = isDark ? 'rgba(242, 106, 244, 0.2)' : 'rgba(242, 106, 244, 0.1)'

  return (
    <YStack gap="$4">
      <Text fontSize="$6" fontWeight="600" fontFamily="$body" color={accentColor}>
        Events
      </Text>

      <YStack gap="$2">
        {[
          <Text fontSize='$4'>In-Person Events</Text>,
          <Text fontSize='$4'>Online Events</Text>,
          <Text fontSize='$4'>Hybrid In-Person & Online Events</Text>,
          <Text fontSize='$4'>Paid Events with Ticketing</Text>,
          <Text fontSize='$4'>Optional Waitlist</Text>,
          <Text fontSize='$4'>Attendance Forms & Approvals</Text>,
          <Text fontSize='$4'>Guild Events</Text>,
          <Text fontSize='$4'>One-Off Personal Events</Text>,
          <Text fontSize='$4'>Message your Attendees</Text>,
          <Text fontSize='$4'>Connect Presentations</Text>,
          <Text fontSize='$4'>Calendar Sync & Export</Text>,
          <Text fontSize='$4'>Beautiful Social Sharing</Text>,
          <Text fontSize='$4'>Embeddable Widgets</Text>,
          <FeatureLinkBulletItem
            href='/api/next/docs'
            label='API Access'
            external
          />,
        ].map((bullet, index) => (
          <XStack gap="$2" key={index}>
            <XStack width={18} minWidth={18} height={18} minHeight={18} backgroundColor={circleBg} alignItems="center" justifyContent="center" borderRadius={9}>
              <Text color={accentColor}>✓</Text>
            </XStack>

            {bullet}
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}

const Presentations: FC = () => {
  const isDark = useThemeName() === 'dark'
  const accentColor = isDark ? '#FBAD7E' : '#F9894B'
  const circleBg = isDark ? 'rgba(249, 137, 75, 0.2)' : 'rgba(249, 137, 75, 0.1)'

  return (
    <YStack gap="$4">
      <Text fontSize="$6" fontWeight="600" fontFamily="$body" color={accentColor}>
        Presentations
      </Text>

      <YStack gap="$2">
        {[
          <Text fontSize='$4'>Manage your Guild's Content Library</Text>,
          <Text fontSize='$4'>Manage your Personal Content Library</Text>,
          <Text fontSize='$4'>Connect Presentations to Events</Text>,
          <Text fontSize='$4'>Connect Presentations to Guilds</Text>,
          <Text fontSize='$4'>Connect Speakers & Bios</Text>,
          <Text fontSize='$4'>Video Links (YouTube, Vimeo)</Text>,
          <Text fontSize='$4'>Curate Content from top Presenters</Text>,
          <Text fontSize='$4'>Share Content across Networks</Text>,
          <Text fontSize='$4'>Embeddable Widgets</Text>,
          <FeatureLinkBulletItem
            href='/api/next/docs'
            label='API Access'
            external
          />,
        ].map((bullet, index) => (
          <XStack gap="$2" key={index}>
            <XStack width={18} minWidth={18} height={18} minHeight={18} backgroundColor={circleBg} alignItems="center" justifyContent="center" borderRadius={9}>
              <Text color={accentColor}>✓</Text>
            </XStack>

            {bullet}
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}
